import React, { useState, useEffect } from 'react';

// Interfaces
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Question) => void;
  initialData?: Question;
}

interface Question {
  question: string;
  answer: string;
  category: string;
  status: string;
  source: string;
  lastUpdated: string;
  id?: string;
}

// Generate Unique ID (replace with your preferred method)
const generateUniqueId = () => {
  return Math.random().toString(36).substring(2, 15); // Simple example
};

// Question Modal Component
const QuestionModal: React.FC<ModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [questionData, setQuestionData] = useState<Question>({
    question: '',
    answer: '',
    category: '',
    status: 'Pending',
    source: '',
    lastUpdated: '',
  });

  useEffect(() => {
    if (initialData) {
      setQuestionData(initialData);
    } else {
      setQuestionData({
        question: '',
        answer: '',
        category: '',
        status: 'Pending',
        source: '',
        lastUpdated: '',
      });
    }
  }, [initialData, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setQuestionData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(questionData);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const isEditing = !!initialData;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50  overflow-scroll">
      <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-3xl overflow-scroll max-h-[90vh]">
        <h2 className="text-xl font-bold mb-4">{isEditing ? 'Update Question' : 'Add New Question'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="question" className="block text-gray-700 font-medium">Question</label>
            <textarea
              id="question"
              name="question"
              value={questionData.question}
              onChange={handleChange}
              className="border border-gray-300 rounded w-full px-3 py-2"
              rows={3}
              required
            />
          </div>
          <div>
            <label htmlFor="answer" className="block text-gray-700 font-medium">Answer</label>
            <textarea
              id="answer"
              name="answer"
              value={questionData.answer}
              onChange={handleChange}
              className="border border-gray-300 rounded w-full px-3 py-2"
              rows={5}
              required
            />
          </div>
          <div>
            <label htmlFor="category" className="block text-gray-700 font-medium">Category</label>
            <input
              type="text"
              id="category"
              name="category"
              value={questionData.category}
              onChange={handleChange}
              className="border border-gray-300 rounded w-full px-3 py-2"
              required
            />
          </div>
          <div>
            <label htmlFor="status" className="block text-gray-700 font-medium">Status</label>
            <select
              id="status"
              name="status"
              value={questionData.status}
              onChange={handleChange}
              className="border border-gray-300 rounded w-full px-3 py-2"
            >
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label htmlFor="source" className="block text-gray-700 font-medium">Source</label>
            <input
              type="text"
              id="source"
              name="source"
              value={questionData.source}
              onChange={handleChange}
              className="border border-gray-300 rounded w-full px-3 py-2"
              required
            />
          </div>
          <div>
            <label htmlFor="lastUpdated" className="block text-gray-700 font-medium">Last Updated</label>
            <input
              type="date"
              id="lastUpdated"
              name="lastUpdated"
              value={questionData.lastUpdated}
              onChange={handleChange}
              className="border border-gray-300 rounded w-full px-3 py-2"
              required
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded mr-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
            >
              {isEditing ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionModal